if (!customElements.get('sa-fst26-tabs')) {
  class SaFst26Tabs extends HTMLElement {
    connectedCallback() {
      this.displayType = this.dataset.displayType || 'carousel';
      this.shell = this.closest('.sa-fst26')?.querySelector('.sa-fst26__shell');
      this.tabList = this.querySelector('.sa-fst26__tablist');
      this.buttons = Array.from(this.querySelectorAll('[data-sa-fst26-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-sa-fst26-panel]'));

      if (!this.buttons.length || !this.panels.length) return;

      this.onTabClick = this.onTabClick.bind(this);
      this.onWindowLoad = this.resyncActivePanel.bind(this);

      this.buttons.forEach((button) => button.addEventListener('click', this.onTabClick));
      this.installStabilityWatchers();

      const initialActive = this.panels.find((panel) => panel.classList.contains('is-active'));
      if (initialActive) {
        this.syncBackground(initialActive);
        this.resyncPanel(initialActive, { resetPosition: true });
      }

      // Swatch/3P scripts can initialize late; reflow once shortly after page load.
      this.scheduleResync(900);

      if (Shopify.designMode) {
        this.addEventListener('shopify:block:select', (event) => {
          const panel = event.target.closest('[data-sa-fst26-panel]');
          if (!panel) return;
          this.activate(panel.dataset.saFst26Panel, true);
        });
      }
    }

    disconnectedCallback() {
      this.buttons?.forEach((button) => button.removeEventListener('click', this.onTabClick));
      window.removeEventListener('load', this.onWindowLoad);
      window.removeEventListener('pageshow', this.onWindowLoad);
      if (this.resyncTimer) clearTimeout(this.resyncTimer);
    }

    onTabClick(event) {
      event.preventDefault();
      this.activate(event.currentTarget.dataset.saFst26Tab, true);
    }

    activate(targetId, smoothScroll) {
      const currentPanel = this.panels.find((panel) => panel.classList.contains('is-active'));
      if (currentPanel && currentPanel.dataset.saFst26Panel === targetId) return;
      const targetPanel = this.panels.find((panel) => panel.dataset.saFst26Panel === targetId);
      if (!targetPanel) return;

      let activeButton = null;

      this.buttons.forEach((button) => {
        const isActive = button.dataset.saFst26Tab === targetId;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-selected', isActive ? 'true' : 'false');
        if (isActive) activeButton = button;
      });

      if (smoothScroll && activeButton && this.tabList) {
        const targetLeft = activeButton.offsetLeft - (this.tabList.clientWidth / 2) + (activeButton.clientWidth / 2);
        this.tabList.scrollTo({
          left: Math.max(0, targetLeft),
          behavior: 'auto'
        });
      }

      this.panels.forEach((panel) => {
        const isActive = panel === targetPanel;
        panel.classList.toggle('is-active', isActive);
        panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });

      this.syncBackground(targetPanel);
      this.resyncPanel(targetPanel, { resetPosition: true });
      document.dispatchEvent(new CustomEvent('sa-fst26:tab-changed', {
        detail: { sectionId: this.closest('.sa-fst26')?.id, panelId: targetId }
      }));
    }

    installStabilityWatchers() {
      window.addEventListener('load', this.onWindowLoad);
      window.addEventListener('pageshow', this.onWindowLoad);
    }

    scheduleResync(delay = 0) {
      if (this.resyncTimer) clearTimeout(this.resyncTimer);
      this.resyncTimer = setTimeout(() => this.resyncActivePanel(), delay);
    }

    getActivePanel() {
      return this.panels.find((panel) => panel.classList.contains('is-active'));
    }

    resyncActivePanel() {
      const active = this.getActivePanel();
      if (!active) return;
      this.resyncPanel(active);
    }

    resyncPanel(panel, options = {}) {
      this.refreshCarousel(panel, options);
      this.refreshProductCards(panel);
    }

    refreshCarousel(panel, options = {}) {
      if (this.displayType !== 'carousel') return;
      const slider = panel.querySelector('slide-show');
      if (!slider || !window.Flickity) return;
      const resetPosition = options.resetPosition === true;

      const attemptRefresh = (attempt = 0) => {
        const flkty = window.Flickity.data(slider);
        if (!flkty) {
          if (attempt < 24) setTimeout(() => attemptRefresh(attempt + 1), attempt < 6 ? 90 : 150);
          return;
        }
        flkty.reloadCells();
        flkty.resize();
        flkty.reposition();
        if (resetPosition && flkty.selectedIndex !== 0) flkty.select(0, false, true);
        if (attempt < 2) {
          setTimeout(() => attemptRefresh(attempt + 1), 120);
          return;
        }
      };

      requestAnimationFrame(() => setTimeout(() => attemptRefresh(0), 60));
    }

    refreshProductCards(panel) {
      if (!panel) return;

      const trigger = () => {
        if (window.lazySizes?.loader?.checkElems) window.lazySizes.loader.checkElems();
        if (window.lazySizes?.autoSizer?.checkElems) window.lazySizes.autoSizer.checkElems();
        document.dispatchEvent(new CustomEvent('sa-fst26:products-refreshed', {
          detail: { sectionId: this.closest('.sa-fst26')?.id, panelId: panel.dataset.saFst26Panel }
        }));
      };

      requestAnimationFrame(trigger);
      setTimeout(trigger, 180);
    }

    syncBackground(panel) {
      if (!this.shell) return;
      const targetBg = panel.dataset.saFst26Bg;
      if (!targetBg) return;
      this.shell.style.setProperty('--sa-fst26-tab-bg', targetBg);
    }
  }

  customElements.define('sa-fst26-tabs', SaFst26Tabs);
}
